import { isStaging } from '@/core/utilities';

const providerNameLabel = {
  'googleads': 'Google Ads',
  'googleAds': 'Google Ads',
  'facebook': 'Facebook',
  'facebookevents': 'Facebook Events'
};

const providerUrlMap = {
  'googleads': 'gads',
  'facebook': 'fb',
  'facebookevents': 'fb',
};

const providerDefaultDelayMap = {
  'adwords': { 'jstag': 1400, 'number': 300 },
  'googleads': { 'jstag': 1400, 'number': 300 },
  'default': 5
};

export default class IntegrationService {
  /** @ngInject **/
  constructor(
    $http,
    ngAuthSettings
  ) {
    this.$http = $http;
    this.ngAuthSettings = ngAuthSettings;
  }

  refreshToken(accountId, integrationId, refreshInfo) {
    if (isStaging) {
      return import(/* webpackChunkName: "staging" */ './MockIntegrationService').then(({ default: mockIntegrationService }) => {
        return mockIntegrationService.refreshToken(accountId, integrationId, refreshInfo);
      });
    }
    return this.$http.post(this.#serviceUserProviderBaseUrl(accountId, integrationId, refreshInfo.type) + '/refreshToken', refreshInfo);
  }

  getIntegrations(accountId) {
    if (isStaging) {
      return import(/* webpackChunkName: "staging" */ './MockIntegrationService').then(({ default: mockIntegrationService }) => {
        return mockIntegrationService.getIntegrations();
      });
    }
    return this.$http.get(this.#serviceUserBaseUrl(accountId));
  }

  updateIntegration(accountId, integration) {
    const clonedIntegration = angular.copy(integration);
    
    if (clonedIntegration.type.toLowerCase() != 'googleads' && clonedIntegration.canManageClients && clonedIntegration.subAccountId) {
      clonedIntegration.platformPrimaryId = clonedIntegration.subAccountId;
    }

    if (isStaging) {
      return import(/* webpackChunkName: "staging" */ './MockIntegrationService').then(({ default: mockIntegrationService }) => {
        return mockIntegrationService.updateIntegration(clonedIntegration.id, this.#sanitizeObject(clonedIntegration));
      });
    }

    return this.$http.patch(this.#serviceUserProviderBaseUrl(accountId, clonedIntegration.id, clonedIntegration.type), this.#sanitizeObject(clonedIntegration));
  }

  getFacebookIntegrationDetails(accountId, integration) {
    return this.$http.get(this.#serviceUserProviderBaseUrl(accountId, integration.id, integration.type) + '/accountdetails');
  }

  getIntegrationAccounts(accountId, integration, mccClientCustomerId) {
    if (isStaging) {
      return import(/* webpackChunkName: "staging" */ './MockIntegrationService').then(({ default: mockIntegrationService }) => {
        return mockIntegrationService.getIntegrationAccounts();
      });
    }
    return this.$http.get(this.#serviceUserProviderBaseUrl(accountId, integration.id, integration.type) + '/accounts' + (mccClientCustomerId ? '?managerClientCustomerId=' + mccClientCustomerId : ''));
  }

  getFbPixels(accountId, integration) {
    if (isStaging) {
      return import(/* webpackChunkName: "staging" */ './MockIntegrationService').then(({ default: mockIntegrationService }) => {
        return mockIntegrationService.getFbPixels();
      });
    }
    return this.$http.get(this.#serviceUserProviderBaseUrl(accountId, integration.id, integration.type) + '/pixels');
  }

  getIntegrationConversionSets(accountId, integration) {
    if (isStaging) {
      return import(/* webpackChunkName: "staging" */ './MockIntegrationService').then(({ default: mockIntegrationService }) => {
        return mockIntegrationService.getIntegrationConversionSets();
      });
    }
    return this.$http.get(this.#serviceUserProviderBaseUrl(accountId, integration.id, integration.type) + '/conversionsets');
  }

  setNumberWithConversionSet(accountId, integration, data) {
    if (isStaging) {
      return import(/* webpackChunkName: "staging" */ './MockIntegrationService').then(({ default: mockIntegrationService }) => {
        return mockIntegrationService.setNumberWithConversionSet();
      });
    }
    return this.$http.post(this.#serviceUserProviderBaseUrl(accountId, integration.id, integration.type) + '/number/conversion', data);
  }

  setJsTagWithConversionSet(accountId, integration, data) {
    return this.$http.post(this.#serviceUserProviderBaseUrl(accountId, integration.id, integration.type) + '/jstag/conversion', data);
  }

  updateIntegrationSetting(accountId, integrationSettingId, integration, data) {
    return this.$http.put(this.#serviceUserProviderBaseUrl(accountId, integrationSettingId, integration.type) + '/conversion', data);
  }

  deleteIntegrationSetting(accountId, integrationSettingId) {
    return this.$http.delete(this.#serviceUserSettingBaseUrl(accountId, integrationSettingId));
  }

  getPlatformLabel(platform) {
    if (platform) {
      return providerNameLabel[platform.toLowerCase()] || '';
    } else {
      return '';
    }
  }

  getProviderUrlMap(provider) {
    return providerUrlMap[provider.toLowerCase()];
  }

  getDefaultDelayTime(provider, type) {
    if (!provider) {
      return;
    }

    var providerDefaultDelay = providerDefaultDelayMap[provider.toLowerCase()];
    
    if (providerDefaultDelay && typeof providerDefaultDelay[type] === 'number') {
      return providerDefaultDelay[type];
    } else {
      return providerDefaultDelayMap.default;
    }
  }

  getReferences(accountId, integration) {
    if (isStaging) {
      return import(/* webpackChunkName: "staging" */ './MockIntegrationService').then(({ default: mockIntegrationService }) => {
        return mockIntegrationService.getReferences();
      });
    }
    return this.$http.get(this.#serviceUserBaseUrl(accountId, integration.id) + '/InboundReferences');
  }

  deleteIntegration(accountId, integration) {
    if (isStaging) {
      return import(/* webpackChunkName: "staging" */ './MockIntegrationService').then(({ default: mockIntegrationService }) => {
        return mockIntegrationService.deleteIntegration(integration.id);
      });
    }
    return this.$http.delete(this.#serviceUserBaseUrl(accountId, integration.id));
  }

  getOauthMessageEvent() {
    if (isStaging) {
      return import(/* webpackChunkName: "staging" */ './MockIntegrationService').then(({ default: mockIntegrationService }) => {
        return mockIntegrationService.getOauthMessageEvent();
      });
    }
    return null;
  }

  getIntegrationAuthType(integrationType) {
    return integrationType === 'facebookEvents' ? 'facebook' : integrationType;
  }

  #sanitizeObject(integration) {
    delete integration.showEditForm;
    delete integration.subAccountId;
    delete integration.canManageClients;
    return integration;
  }

  #serviceUserBaseUrl(accountId, integrationId) {
    return this.ngAuthSettings.apiServiceBaseUri + accountId + '/integrations' + (integrationId ? '/' + integrationId : '');
  }

  #serviceUserProviderBaseUrl(accountId, integrationId, provider) {
    return this.ngAuthSettings.apiServiceBaseUri + accountId + '/integrations/' + providerUrlMap[provider.toLowerCase()] + '/' + integrationId;
  }

  #serviceUserSettingBaseUrl(accountId, integrationId) {
    return this.ngAuthSettings.apiServiceBaseUri + accountId + '/integrations/integrationsetting/' + integrationId;
  }
}